import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ConditionalProperty } from 'Automations/nodeDefinitions/conditions'
import { matchesNodeId } from 'Automations/nodeDefinitions/helpers'
import {
  previousApprovalAttachments,
  previousApprovalPaymentTerms,
  previousApprovalPricing,
  previousApprovalRawText
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import {
  formatQuoteComparison,
  formatQuotePricingSubOperator,
  formatQuotePricingSubject
} from '../../../../switch/components/GroupEditor/inputs'
import {
  ATTACHMENTS_COMPARISON_LABELS,
  PAYMENT_TERMS_COMPARISON_LABELS
} from '../quoteComparison'
import { PreviousApprovalPropertyEditor } from './PreviousApprovalPropertyEditor'
import { formatPreviousApprovalSubject } from './PreviousApprovalSubject'
import { PREVIOUS_APPROVAL_LABEL } from './previousApproval.constants'
import { previousApprovalSupportingTask } from './previousApproval.supportingTask'
import { readRow } from './PreviousApprovalPropertyEditor.helpers'

// Every field on the approval stores the same compound value, so one validator
// serves them all. Each missing piece returns its own result tagged with
// `metadata.field`, which the property editor reads to attach the error to the
// correct sub-input.
const validatePreviousApprovalValue = ({
  value,
  downstreamNodes
}: {
  value: unknown
  downstreamNodes: AutomationsPresentationV1Node[]
}) => {
  const { reviewNodeId, subOperator } = readRow(value)
  const results = []
  if (!reviewNodeId) {
    results.push({
      title: 'Incomplete condition',
      message: 'Review required',
      metadata: { field: 'reviewNodeId' as const }
    })
  } else if (!leadsToReviewNode({ downstreamNodes, reviewNodeId })) {
    // The approval has to have happened by the time the switch reads it, so a
    // step the switch doesn't lead to can never have one.
    results.push({
      title: 'Unreachable review',
      message: 'Review must come after this switch',
      metadata: { field: 'reviewNodeId' as const }
    })
  }
  if (!subOperator) {
    results.push({
      title: 'Incomplete condition',
      message: 'Operator required',
      metadata: { field: 'operator' as const }
    })
  }
  return results.length ? results : undefined
}

/** True when the switch leads to the chosen step, by any branch. */
const leadsToReviewNode = ({
  downstreamNodes,
  reviewNodeId
}: {
  downstreamNodes: AutomationsPresentationV1Node[]
  reviewNodeId: string
}): boolean =>
  downstreamNodes.some(
    node => node.type === 'REVIEW_QUOTE' && matchesNodeId(node.id, reviewNodeId)
  )

// The outcome every comparison gates on, so a quote never approved — or last
// rejected — compares as false rather than as unchanged.
const outcomeSemanticType = semanticTypes.sequence.previousQuoteApproval.outcome

export const previousApprovalConditionalProperty: ConditionalProperty = {
  label: PREVIOUS_APPROVAL_LABEL,
  // Names the review step ahead of the subject, so two rows on different steps
  // read apart on the canvas.
  formatPreviewProperty: formatPreviousApprovalSubject,
  focusOperatorOnSelect: false,
  propertyEditorComponent: PreviousApprovalPropertyEditor,
  // A row compares against a review step, so a workflow with none has nothing
  // to offer. Several rows stay allowed: each is scoped to a different step or
  // field.
  propertyAvailable: ({ nodes }) =>
    nodes.some(node => node.type === 'REVIEW_QUOTE'),
  validateProperty: validatePreviousApprovalValue,
  operators: {
    ATTACHMENTS: {
      label: 'Attachments',
      // The quote side of the comparison — genuinely published by the start
      // node, so the usual upstream gating applies. The approved side is
      // supplied by `valueSupportingTask` and reached via factory config.
      valueSemanticType: semanticTypes.sequence.quote.attachments,
      functionString: previousApprovalAttachments({
        approvedSemanticType:
          semanticTypes.sequence.previousQuoteApproval.attachments,
        outcomeSemanticType
      }),
      // The operator label supplies the field and the value formatter the
      // comparison, so the row reads "Previously approved quote attachments are
      // equal". Both dropdown and preview read the same labels.
      formatPreviewValue: formatQuoteComparison(ATTACHMENTS_COMPARISON_LABELS)
    },
    RAW_TEXT: {
      label: 'Raw text',
      valueSemanticType: semanticTypes.sequence.quote.rawText,
      functionString: previousApprovalRawText({
        approvedSemanticType:
          semanticTypes.sequence.previousQuoteApproval.rawText,
        outcomeSemanticType
      }),
      // Singular subject, so the default labels apply: the canvas reads
      // "Previously approved quote raw text is equal".
      formatPreviewValue: formatQuoteComparison()
    },
    PAYMENT_TERMS: {
      label: 'Payment terms',
      valueSemanticType: semanticTypes.sequence.quote.paymentTerms,
      functionString: previousApprovalPaymentTerms({
        approvedSemanticType:
          semanticTypes.sequence.previousQuoteApproval.paymentTerms,
        outcomeSemanticType
      }),
      // Plural subject, so the canvas reads "<step> previous approval payment
      // terms are equal".
      formatPreviewValue: formatQuoteComparison(PAYMENT_TERMS_COMPARISON_LABELS)
    },
    PRICING: {
      label: 'Pricing',
      // A quote's pricing lives entirely in its phases, so this gates the
      // operator on the quote publishing them — which is also what the
      // supporting task feeds the comparison executor as its source.
      valueSemanticType: semanticTypes.sequence.quote.phases,
      functionString: previousApprovalPricing({
        comparisonSemanticTypes: {
          ALL: semanticTypes.sequence.quotePricingComparison.allMatch,
          PRICES: semanticTypes.sequence.quotePricingComparison.pricesMatch,
          DISCOUNTS:
            semanticTypes.sequence.quotePricingComparison.discountsMatch,
          MINIMUMS: semanticTypes.sequence.quotePricingComparison.minimumsMatch
        },
        outcomeSemanticType
      }),
      // The chosen comparison is the subject, so it replaces the field in the
      // clause: "<approval> prices are equal", "<approval> contract terms are
      // equal".
      formatPreviewOperator: formatQuotePricingSubject,
      formatPreviewValue: formatQuotePricingSubOperator
    }
  },
  valueSupportingTask: previousApprovalSupportingTask
}
